/**
 * 来場者スタンプ帳サービス
 * 来場者本人の企画達成(スタンプ)状況取得を提供します
 */

import { getSupabaseClient } from '../../../services/supabase/client';
import { AppError } from '../../../shared/utils/errorHandling';
import { logError } from '../../../shared/utils/logger';
import { validateRequiredString } from '../../../shared/utils/validation';
import { loadVisitorSession } from './visitorSessionService';
import { loadLocalEventVisits, replaceLocalEventVisits } from './visitorStampCacheService';

/** スタンプ取得RPC名 */
const VISITOR_GET_EVENT_VISITS_RPC_NAME = 'visitor_get_event_visits';

/** 入力不正エラーコード */
export const VISITOR_EVENT_VISITS_INVALID_INPUT_CODE = 'VISITOR_EVENT_VISITS_INVALID_INPUT';

/** セッション不正エラーコード */
export const VISITOR_EVENT_VISITS_SESSION_INVALID_CODE = 'VISITOR_EVENT_VISITS_SESSION_INVALID';

/** 取得失敗エラーコード */
export const VISITOR_EVENT_VISITS_FAILED_CODE = 'VISITOR_EVENT_VISITS_FAILED';

/**
 * RPCエラーに特定メッセージが含まれるか判定する
 * @param {{ message?: string } | null} rpcError - RPCエラー
 * @param {string} keyword - 判定キーワード
 * @returns {boolean} 含まれる場合true
 */
const includesRpcErrorMessage = (rpcError, keyword) => {
  if (!rpcError?.message) {
    return false;
  }

  return rpcError.message.includes(keyword);
};

/**
 * RPCエラーをアプリエラーコードへ変換する
 * @param {{ message?: string } | null} rpcError - RPCエラー
 * @returns {string} 変換済みエラーコード
 */
const mapVisitorEventVisitsRpcErrorCode = (rpcError) => {
  if (includesRpcErrorMessage(rpcError, 'INVALID_SESSION_TOKEN')) {
    return VISITOR_EVENT_VISITS_INVALID_INPUT_CODE;
  }

  if (
    includesRpcErrorMessage(rpcError, 'SESSION_NOT_FOUND')
    || includesRpcErrorMessage(rpcError, 'VISITOR_INACTIVE')
  ) {
    return VISITOR_EVENT_VISITS_SESSION_INVALID_CODE;
  }

  return VISITOR_EVENT_VISITS_FAILED_CODE;
};

/**
 * visitor_get_event_visits の1行データを検証する
 * @param {any} visitRow - 達成状況行
 * @returns {{ eventId: string, claimedAt: string }} 検証済み達成状況
 * @throws {AppError} レスポンス不正の場合
 */
const validateEventVisitRow = (visitRow) => {
  const eventId = visitRow?.event_id;
  const claimedAt = visitRow?.claimed_at;

  if (!eventId || !claimedAt) {
    throw new AppError('visitor_get_event_visits のレスポンス形式が不正です。', {
      code: VISITOR_EVENT_VISITS_FAILED_CODE,
      statusCode: 500,
      details: { visitRow },
    });
  }

  return {
    eventId,
    claimedAt,
  };
};

/**
 * claimedAt を比較用の数値へ変換する
 * @param {string} claimedAt - 押印日時
 * @returns {number | null} 比較用タイムスタンプ
 */
const toClaimedTimestamp = (claimedAt) => {
  if (typeof claimedAt !== 'string' || claimedAt.trim() === '') {
    return null;
  }

  const parsedTimestamp = Date.parse(claimedAt);
  return Number.isNaN(parsedTimestamp) ? null : parsedTimestamp;
};

/**
 * サーバー履歴とローカル履歴を eventId ごとに新しい claimedAt で統合する
 * @param {Array<{ eventId: string, claimedAt: string }>} remoteVisits - サーバー履歴
 * @param {Array<{ eventId: string, claimedAt: string }>} localVisits - ローカル履歴
 * @returns {Array<{ eventId: string, claimedAt: string }>} 統合済み履歴
 */
const mergeEventVisits = (remoteVisits, localVisits) => {
  /** 統合済み履歴一覧 */
  const mergedVisits = new Map();

  [...(Array.isArray(remoteVisits) ? remoteVisits : []), ...(Array.isArray(localVisits) ? localVisits : [])]
    .forEach((visit) => {
      if (typeof visit?.eventId !== 'string' || visit.eventId.trim() === '') {
        return;
      }

      /** 既存履歴 */
      const currentVisit = mergedVisits.get(visit.eventId) ?? null;

      /** 現在のタイムスタンプ */
      const currentTimestamp = toClaimedTimestamp(currentVisit?.claimedAt);

      /** 比較対象タイムスタンプ */
      const nextTimestamp = toClaimedTimestamp(visit?.claimedAt);

      if (
        !currentVisit
        || currentTimestamp === null
        || (nextTimestamp !== null && nextTimestamp >= currentTimestamp)
      ) {
        mergedVisits.set(visit.eventId, {
          eventId: visit.eventId,
          claimedAt: visit.claimedAt,
        });
      }
    });

  return Array.from(mergedVisits.values());
};

/**
 * 来場者本人のスタンプ状況を取得する
 * @param {string} sessionToken - 来場者セッショントークン
 * @returns {Promise<Array<{ eventId: string, claimedAt: string }>>} 達成済みイベント一覧
 * @throws {AppError} 入力不正の場合
 */
export const visitorGetEventVisits = async (sessionToken) => {
  const sessionTokenValidation = validateRequiredString(sessionToken, 'セッショントークン');

  if (!sessionTokenValidation.isValid) {
    throw new AppError(sessionTokenValidation.message, {
      code: VISITOR_EVENT_VISITS_INVALID_INPUT_CODE,
      statusCode: 400,
    });
  }

  const supabaseClient = getSupabaseClient();

  const { data, error } = await supabaseClient.rpc(VISITOR_GET_EVENT_VISITS_RPC_NAME, {
    p_session_token: sessionToken.trim(),
    p_limit: 1000,
  });

  if (error) {
    const mappedErrorCode = mapVisitorEventVisitsRpcErrorCode(error);

    logError('visitor_get_event_visits RPC 実行に失敗しました。', {
      code: error.code,
      message: error.message,
      details: error.details,
      mappedErrorCode,
    });

    throw new AppError('スタンプ状況の取得に失敗しました。', {
      code: mappedErrorCode,
      statusCode:
        mappedErrorCode === VISITOR_EVENT_VISITS_SESSION_INVALID_CODE
          ? 401
          : 500,
      details: {
        message: error.message,
      },
    });
  }

  if (!Array.isArray(data)) {
    logError('visitor_get_event_visits のレスポンス形式が不正です。', {
      details: { data },
    });

    throw new AppError('スタンプ状況の取得に失敗しました。', {
      code: VISITOR_EVENT_VISITS_FAILED_CODE,
      statusCode: 500,
      details: { data },
    });
  }

  const validatedVisits = data.map(validateEventVisitRow);

  // RPCが利用できる環境では、結果を端末へ保存して次回以降の表示を安定化させる
  try {
    const storedSession = await loadVisitorSession();
    const visitorId = storedSession?.visitorId ?? '';
    if (visitorId) {
      const localVisits = await loadLocalEventVisits(visitorId);
      await replaceLocalEventVisits(visitorId, mergeEventVisits(validatedVisits, localVisits));
    }
  } catch (cacheError) {
    logError('スタンプ帳キャッシュ更新に失敗しました。', {
      errorMessage: cacheError instanceof Error ? cacheError.message : String(cacheError),
    });
  }

  return validatedVisits;
};
